import "./Services.scss";
import { useEffect, useState, useRef } from "react";
import { Typography, Button, Alert } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useLanguage } from "../../context/LanguageContext";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import Loader from "../../components/Loader/Loader";
import BookingModal from "../../components/BookingModal/BookingModal";
import DepartmentSelection from "./DepartmentSelection";
import ServicesList from "./ServicesList";
import UtilityItemsList from "./UtilityItemsList";
import NotesSection from "./NotesSection";
import PriceSummary from "./PriceSummary";
import SuggestServiceModal from "./SuggestServiceModal";
import Map from "../../components/Map/Map";
import AddIcon from "@mui/icons-material/AddBoxOutlined";
import ArrowDownwardIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import Address from "../../assets/images/address-icon.png";
import LocationIcon from "../../assets/images/location-icon.png";

const Services = () => {
  const [searchState, setSearchState] = useState("departments");
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedUtilityItems, setSelectedUtilityItems] = useState([]);
  const [serviceQuantities, setServiceQuantities] = useState({});
  const [utilityQuantities, setUtilityQuantities] = useState({});
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [services, setServices] = useState([]);
  const [utilityItems, setUtilityItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [openMap, setOpenMap] = useState(false);
  const [addressOpen, setAddressOpen] = useState(false);

  const navigate = useNavigate();
  const { language } = useLanguage();
  const { addServices, clearCart, minimumPrice, updateMinimumPrice, selectedAddress, updateSelectedAddress } = useCart();
  const { user } = useAuth();
  const { category: pathCategory } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryCategory = queryParams.get("category");
  const addressRef = useRef(null);

  const categoryId = pathCategory || queryCategory;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Initialize component - fetch departments or specific category
  useEffect(() => {
    const initializeComponent = async () => {
      setLoading(true);
      
      try {
        if (categoryId) {
          clearCart();
          
          const response = await api.get(`/categories/${categoryId}`);
          const categoryData = response?.data?.data;
          
          setSelectedDepartment(categoryData);
          setServices(categoryData?.services || []);
          setUtilityItems(categoryData?.utilityItems || []);
          
          const minPrice = response?.data?.minimumPrice || 0;
          updateMinimumPrice(minPrice);
          
          setSearchState("services");
        } else {
          const response = await api.get("/categories");
          setDepartments(response?.data?.data);
          setSearchState("departments");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    initializeComponent();
  }, [categoryId]);

  // Fetch category with services
  const fetchCategoryWithServices = async (categoryId) => {
    try {
      setLoading(true);
      const response = await api.get(`/categories/${categoryId}`);
      const categoryData = response?.data?.data;
      
      setServices(categoryData?.services || []);
      setUtilityItems(categoryData?.utilityItems || []);
      
      const minPrice = response?.data?.minimumPrice || 0;
      updateMinimumPrice(minPrice);
    } catch (err) {
      console.error("Error fetching category with services:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDepartmentSelect = (department) => {
    clearCart();
    setSelectedDepartment(department);
    setSelectedServices([]);
    setSelectedUtilityItems([]);
    setServiceQuantities({});
    setUtilityQuantities({});
    setNotes("");
    fetchCategoryWithServices(department._id);
    setSearchState("services");
  };

  const handleServiceToggle = (service) => {
    setSelectedServices((prev) => {
      if (prev.some((s) => s._id === service._id)) {
        setServiceQuantities((prevQuantities) => {
          const newQuantities = { ...prevQuantities };
          delete newQuantities[service._id];
          return newQuantities;
        });
        return prev.filter((s) => s._id !== service._id);
      } else {
        setServiceQuantities((prevQuantities) => ({
          ...prevQuantities,
          [service._id]: 1,
        }));
        return [...prev, service];
      }
    });
  };

  const handleUtilityToggle = (item) => {
    setSelectedUtilityItems((prev) => {
      if (prev.some((u) => u._id === item._id)) {
        setUtilityQuantities((prevQuantities) => {
          const newQuantities = { ...prevQuantities };
          delete newQuantities[item._id];
          return newQuantities;
        });
        return prev.filter((u) => u._id !== item._id);
      } else {
        setUtilityQuantities((prevQuantities) => ({
          ...prevQuantities,
          [item._id]: 1,
        }));
        return [...prev, item];
      }
    });
  };

  const handleQuantityChange = (itemId, delta, isUtility = false) => {
    const setQuantities = isUtility ? setUtilityQuantities : setServiceQuantities;
    const setSelected = isUtility ? setSelectedUtilityItems : setSelectedServices;

    setQuantities((prevQuantities) => {
      const currentQuantity = prevQuantities[itemId] || 0;
      const newQuantity = Math.max(0, currentQuantity + delta);

      if (newQuantity === 0) {
        setSelected((prev) => prev.filter((item) => item._id !== itemId));
        const newQuantities = { ...prevQuantities };
        delete newQuantities[itemId];
        return newQuantities;
      }

      return {
        ...prevQuantities,
        [itemId]: newQuantity,
      };
    });
  };

  const handleBackToDepartments = () => {
    if (categoryId) {
      navigate(-1);
    } else {
      setSelectedDepartment(null);
      setSelectedServices([]);
      setSelectedUtilityItems([]);
      setServiceQuantities({});
      setUtilityQuantities({});
      setNotes("");
      setSearchState("departments");
    }
  };

  // Price calculations
  const calculateServicesPrice = () => {
    return selectedServices.reduce((total, service) => {
      const quantity = serviceQuantities[service._id] || 1;
      return total + service.price * quantity;
    }, 0);
  };

  const calculateUtilityPrice = () => {
    return selectedUtilityItems.reduce((total, item) => {
      const quantity = utilityQuantities[item._id] || 1;
      return total + item.price * quantity;
    }, 0);
  };

  const calculateTotalPrice = () => {
    return calculateServicesPrice() + calculateUtilityPrice();
  };

  const getTotalItemsCount = () => {
    const servicesCount = Object.values(serviceQuantities).reduce(
      (total, quantity) => total + quantity,
      0
    );
    const utilityCount = Object.values(utilityQuantities).reduce(
      (total, quantity) => total + quantity,
      0
    );
    return servicesCount + utilityCount;
  };

  // Validation logic
  const getValidationErrors = () => {
    const errors = [];
    const servicesPrice = calculateServicesPrice();
    const hasServices = selectedServices.length > 0;
    const hasUtilityItems = selectedUtilityItems.length > 0;

    if (!hasServices && hasUtilityItems) {
      errors.push({
        type: "no_services",
        message: {
          ar: "يجب اختيار خدمة واحدة على الأقل مع المستلزمات",
          en: "You must select at least one service with utility items"
        }
      });
    }

    if (hasServices && minimumPrice > 0 && servicesPrice < minimumPrice) {
      errors.push({
        type: "minimum_price",
        message: {
          ar: `الحد الأدنى للخدمات ${minimumPrice} ر.س. المبلغ الحالي ${servicesPrice} ر.س (المستلزمات لا تحتسب)`,
          en: `Minimum services amount is ${minimumPrice} SAR. Current amount is ${servicesPrice} SAR (utility items not included)`
        }
      });
    }

    if (!hasServices && !hasUtilityItems) {
      errors.push({
        type: "empty_cart",
        message: {
          ar: "يجب اختيار خدمة واحدة على الأقل",
          en: "You must select at least one service"
        }
      });
    }

    // Address validation
    if ((hasServices || hasUtilityItems) && !selectedAddress) {
      errors.push({
        type: "no_address",
        message: {
          ar: "العنوان مطلوب",
          en: "Address is required"
        }
      });
    }

    return errors;
  };

  const isBookingAllowed = () => {
    return getValidationErrors().length === 0;
  };

  const handleBookForLater = () => {
    if (!isBookingAllowed()) {
      return;
    }
    setShowBookingModal(true);
  };

  const handleProceedToBooking = () => {
    if (!isBookingAllowed()) {
      return;
    }

    const servicesWithQuantities = selectedServices.map((service) => ({
      ...service,
      quantity: serviceQuantities[service._id] || 1,
      notes: notes,
      type: "service"
    }));

    const utilityItemsWithQuantities = selectedUtilityItems.map((item) => ({
      ...item,
      quantity: utilityQuantities[item._id] || 1,
      notes: notes,
      type: "utility"
    }));

    const allItems = [...servicesWithQuantities, ...utilityItemsWithQuantities];

    if (allItems.length > 0) {
      const categoryIdToUse = selectedDepartment?._id || categoryId;
      addServices(allItems, categoryIdToUse, notes, minimumPrice);
      navigate("/cart");
    } else {
      navigate("/cart");
    }
  };

  const handleBookingComplete = () => {
    handleProceedToBooking();
  };

  // Address handling functions
  const userAddresses = user?.address || [];
  const hasUserAddresses = userAddresses.length > 0;

  const handleAddressToggle = () => {
    setAddressOpen(!addressOpen);
  };

  const handleClickAway = (event) => {
    if (addressRef.current && !addressRef.current.contains(event.target)) {
      setAddressOpen(false);
    }
  };

  const handleAddressSelection = (index) => {
    const addressData = userAddresses[index];
    updateSelectedAddress(addressData);
  };

  const formatUserAddress = (addressObj) => {
    if (!addressObj) return "";

    if (language === "ar") {
      return `${addressObj.city || ""}، ${addressObj.region || ""}، شارع ${
        addressObj.street || ""
      }، مبنى ${addressObj.buildingNo || ""}، طابق ${addressObj.floorNo || ""}`;
    } else {
      return `${addressObj.city || ""}, ${addressObj.region || ""}, ${
        addressObj.street || ""
      } St., Building ${addressObj.buildingNo || ""}, Floor ${
        addressObj.floorNo || ""
      }`;
    }
  };

  // Initialize selectedAddress from user addresses if it's not set yet
  useEffect(() => {
    if (hasUserAddresses && userAddresses.length > 0) {
      if (!selectedAddress) {
        updateSelectedAddress(userAddresses[0]);
      } else {
        const addressStillExists = userAddresses.some(
          (addr) => addr._id === selectedAddress._id
        );
        if (!addressStillExists) {
          updateSelectedAddress(userAddresses[0]);
        }
      }
    }
  }, [hasUserAddresses, selectedAddress, userAddresses, updateSelectedAddress]);

  // Handle click away for address dropdown
  useEffect(() => {
    if (addressOpen) {
      document.addEventListener("mousedown", handleClickAway);
    } else {
      document.removeEventListener("mousedown", handleClickAway);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickAway);
    };
  }, [addressOpen]);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="services">
      <div className="container">
        <div className="services-container">
          <div className="worker-search">
            {/* Department Selection View */}
            {searchState === "departments" && !categoryId && (
              <DepartmentSelection
                departments={departments}
                loading={loading}
                language={language}
                onDepartmentSelect={handleDepartmentSelect}
                LoaderComponent={Loader}
              />
            )}

            {/* Services Selection View */}
            {searchState === "services" && selectedDepartment && (
              <div className="services-container-booking">
                <div className="worker-search-header">
                  {language === "ar" ? (
                    <Button
                      startIcon={<ArrowForwardIcon />}
                      onClick={handleBackToDepartments}
                      className="back-button"
                    >
                      الرجوع للأقسام
                    </Button>
                  ) : (
                    <Button
                      startIcon={<ArrowBackIcon />}
                      onClick={handleBackToDepartments}
                      className="back-button"
                    >
                      Back to Departments
                    </Button>
                  )}

                  <Typography variant="h5" component="h2" className="search-title">
                    {language === "ar"
                      ? `خدمات ${selectedDepartment.name?.ar || ""}`
                      : `${selectedDepartment.name?.en || ""} Services`}
                  </Typography>
                </div>

                {parseFloat(minimumPrice) > 0 && (
                  <Alert
                    severity="info"
                    sx={{
                      margin: "10px 0",
                      "& .MuiAlert-message": {
                        fontSize: "14px",
                        direction: language === "ar" ? "rtl" : "ltr",
                      },
                    }}
                  >
                    {language === "ar"
                      ? `الحد الأدنى للطلب: ${minimumPrice} ر.س`
                      : `Minimum order amount: ${minimumPrice} SAR`}
                  </Alert>
                )}

                {loading ? (
                  <Loader />
                ) : (
                  <>
                    {/* Services List */}
                    <ServicesList
                      services={services}
                      language={language}
                      selectedServices={selectedServices}
                      serviceQuantities={serviceQuantities}
                      onServiceToggle={handleServiceToggle}
                      onQuantityChange={handleQuantityChange}
                      onSuggestClick={() => setShowSuggestModal(true)}
                    />

                    {/* Utility Items List */}
                    <UtilityItemsList
                      utilityItems={utilityItems}
                      language={language}
                      selectedUtilityItems={selectedUtilityItems}
                      utilityQuantities={utilityQuantities}
                      onUtilityToggle={handleUtilityToggle}
                      onQuantityChange={handleQuantityChange}
                    />

                    {/* Notes Section */}
                    {(selectedServices.length > 0 || selectedUtilityItems.length > 0) && (
                      <>
                        <NotesSection
                          notes={notes}
                          onNotesChange={setNotes}
                          language={language}
                        />

                        <div className="section-container">
                                                     <Typography
        variant="h6"
        component="h3"
        style={{
          marginBottom: "10px",
          fontWeight: "bold",
          color: "#333",
        }}
      >
        {language === "ar" ? "اختر العنوان" : "Select Address"}
      </Typography>
                            {/* Address Selection Section */}
                        <div className="info-container-address" onClick={handleAddressToggle}>
                          <img src={Address} alt="" />
                          {selectedAddress ? (
                            <p className="selected-address-container">
                              <span className="selected-address-name">
                                {selectedAddress.name}
                              </span>
                              {formatUserAddress(selectedAddress)}
                            </p>
                          ) : (
                            <p>
                              <span>
                                {language === "ar" ? "لا يوجد عنوان" : "No Address"}
                              </span>
                              {language === "ar"
                                ? " قم بالضغط لاضافة عنوان"
                                : " Click to add address"}
                            </p>
                          )}

                          <ArrowDownwardIcon className="icon" />

                          {addressOpen && (
                            <div ref={addressRef} className="address-container">
                              {hasUserAddresses ? (
                                userAddresses.map((address, index) => (
                                  <div
                                    className={`address-item ${
                                      selectedAddress && selectedAddress._id === address._id
                                        ? "selected"
                                        : ""
                                    }`}
                                    key={address._id || index}
                                    onClick={() => handleAddressSelection(index)}
                                  >
                                    <div className="img-container">
                                      <img src={LocationIcon} alt="" />
                                    </div>
                                    <div className="text-container">
                                      <p>{address.name}</p>
                                      <span>{formatUserAddress(address)}</span>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="no-address-message">
                                  <p>
                                    {language === "ar"
                                      ? "لا توجد عناوين محفوظة"
                                      : "No saved addresses"}
                                  </p>
                                </div>
                              )}

                              <div
                                className="address-header"
                                onClick={() => {
                                  setOpenMap(true);
                                }}
                              >
                                <AddIcon />
                                <p className="add-address">
                                  {language === "ar" ? "إضافة عنوان جديد" : "Add new address"}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                        <Map open={openMap} onClose={() => setOpenMap(false)} />
                        </div>
                        
   
  
                      </>
                    )}
                    

                    {/* Price Summary and Actions */}
                    {(selectedServices.length > 0 || selectedUtilityItems.length > 0) && (
                      <PriceSummary
                        language={language}
                        totalPrice={calculateTotalPrice()}
                        servicesPrice={calculateServicesPrice()}
                        utilityPrice={calculateUtilityPrice()}
                        totalItemsCount={getTotalItemsCount()}
                        hasServices={selectedServices.length > 0}
                        hasUtilityItems={selectedUtilityItems.length > 0}
                        validationErrors={getValidationErrors()}
                        isBookingAllowed={isBookingAllowed()}
                        onBookForLater={handleBookForLater}
                      />
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Booking Modal */}
          <BookingModal
            open={showBookingModal}
            onClose={() => setShowBookingModal(false)}
            selectedDepartment={selectedDepartment}
            selectedAddress={selectedAddress}
            selectedServices={[
              ...selectedServices.map((service) => ({
                ...service,
                quantity: serviceQuantities[service._id] || 1,
                notes: notes,
                type: "service"
              })),
              ...selectedUtilityItems.map((item) => ({
                ...item,
                quantity: utilityQuantities[item._id] || 1,
                notes: notes,
                type: "utility"
              }))
            ]}
            notes={notes}
            onBookingComplete={handleBookingComplete}
          />

          {/* Suggest Service Modal */}
          <SuggestServiceModal
            open={showSuggestModal}
            onClose={() => setShowSuggestModal(false)}
            categoryId={selectedDepartment?._id || categoryId}
            language={language}
          />
        </div>
      </div>
    </div>
  );
};

export default Services;